import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  RocketLaunch as RocketIcon,
  Group as GroupIcon,
  Tune as TuneIcon,
  LocalOffer as PriceIcon,
  ArrowForward as ArrowIcon,
  AutoAwesome as SparkIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import { CheckCircleOutline as CheckIcon } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import BrandLogo from './BrandLogo';

const COLORS = {
  bgTop: '#01020b',
  bgBottom: '#01010b',
  border: 'rgba(168,85,247,0.18)',
  cardBg: 'rgba(13, 15, 24, 0.55)',
  cardBg2: 'rgba(10, 12, 18, 0.55)',
  textMuted: 'rgba(233,231,255,0.72)',
};

const navLinkSx = {
  color: 'rgba(233,231,255,0.72)',
  fontWeight: 650,
  fontSize: 12.5,
  letterSpacing: 0.2,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': { color: 'rgba(233,231,255,0.95)' },
};

const pillBtnSx = { borderRadius: 999, px: 2.25, py: 0.85 };

const surfaceCardSx = {
  borderRadius: 3.5,
  border: `1px solid ${COLORS.border}`,
  backgroundColor: COLORS.cardBg,
  backdropFilter: 'blur(10px)',
};

const featuredPanelSx = {
  ...surfaceCardSx,
  mt: 3,
  position: 'relative',
  overflow: 'hidden',
  backgroundImage:
    'radial-gradient(1200px 520px at 75% 40%, rgba(168,85,247,0.10) 0%, rgba(0,0,0,0) 55%), linear-gradient(180deg, rgba(13, 15, 24, 0.72) 0%, rgba(8, 9, 14, 0.72) 100%)',
  boxShadow: '0 18px 80px rgba(0,0,0,0.55)',
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 18,
    right: 18,
    bottom: 12,
    height: 3,
    borderRadius: 999,
    background: 'linear-gradient(90deg, rgba(168,85,247,0.0) 0%, rgba(168,85,247,0.55) 35%, rgba(34,211,238,0.35) 65%, rgba(34,211,238,0.0) 100%)',
    filter: 'blur(0.2px)',
    opacity: 0.75,
    pointerEvents: 'none',
  },
};

function EthereumGraphic() {
  const [imgOk, setImgOk] = useState(true);
  return (
    <Box
      sx={{
        height: { xs: 320, md: 420 },
        width: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        // Keep hero graphic clean (no background glow bleeding through).
        backgroundColor: '#01020b',
      }}
    >
      {imgOk ? (
        <Box
          component="img"
          src="/assets/1.png"
          alt=""
          onError={() => setImgOk(false)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      ) : null}
    </Box>
  );
}

const LandingPage = ({ onSignIn, onSignUp }) => {
  const reduceMotion = useReducedMotion();
  const rootRef = useRef(null);

  // Subtle mouse spotlight (no background drift).
  useEffect(() => {
    if (reduceMotion) return;
    const el = rootRef.current;
    if (!el) return;

    let raf = 0;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty('--mx', `${x}%`);
        el.style.setProperty('--my', `${y}%`);
      });
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
    };
  }, [reduceMotion]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(180deg, #01020b 0%, #01010b 100%)',
        position: 'relative',
        overflow: 'hidden',
        '--mx': '60%',
        '--my': '35%',
      }}
    >
      {/* Mouse spotlight */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          opacity: 0.9,
          backgroundImage:
            'radial-gradient(520px 360px at var(--mx) var(--my), rgba(168,85,247,0.20) 0%, rgba(0,0,0,0) 60%), radial-gradient(520px 360px at calc(var(--mx) + 12%) calc(var(--my) + 18%), rgba(34,211,238,0.10) 0%, rgba(0,0,0,0) 65%)',
          transition: reduceMotion ? 'none' : 'opacity 220ms ease',
        }}
      />

      {/* Top nav */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(1,2,11,0.78)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container maxWidth="lg" sx={{ py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <BrandLogo size={28} showText={false} />
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', display: { xs: 'none', md: 'flex' } }}>
              <Typography sx={navLinkSx} onClick={() => scrollTo('features')}>Features</Typography>
              <Typography sx={navLinkSx} onClick={() => scrollTo('how')}>How it Works</Typography>
              <Typography sx={navLinkSx} onClick={() => scrollTo('faq')}>FAQ</Typography>
              <Typography sx={navLinkSx} onClick={() => scrollTo('pricing')}>Pricing</Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button variant="outlined" onClick={onSignIn} sx={pillBtnSx}>
                Sign in
              </Button>
              <Button variant="contained" onClick={onSignUp} sx={pillBtnSx}>
                Create Account
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 }, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 950,
                lineHeight: 1.02,
                letterSpacing: -0.6,
              }}
            >
              Bundle Supply.
              <br />
              Launch Faster.
              <br />
              <Box
                component="span"
                sx={{
                  backgroundImage:
                    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 30%, rgba(34, 211, 238, 1) 60%, rgba(168, 85, 247, 1) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundSize: '220% 100%',
                  animation: reduceMotion ? 'none' : 'gradientShift 6s ease-in-out infinite',
                  '@keyframes gradientShift': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                }}
              >
                Control Everything.
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, maxWidth: 520, color: COLORS.textMuted }}>
              Easily launch tokens on Ethereum and bundle supply to up to 50 wallets. Manage everything from an easy to use control panel.
            </Typography>

            <Stack direction="row" spacing={1.25} sx={{ mt: 3, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={onSignUp}
                endIcon={<ArrowIcon />}
                sx={{
                  ...pillBtnSx,
                  px: 2.6,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 18px 70px rgba(168,85,247,0.18)',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: -2,
                    background:
                      'linear-gradient(90deg, rgba(168, 85, 247, 0.0) 0%, rgba(34, 211, 238, 0.22) 50%, rgba(168, 85, 247, 0.0) 100%)',
                    transform: 'translateX(-70%)',
                    animation: reduceMotion ? 'none' : 'ctaSheen 2.8s ease-in-out infinite',
                  },
                  '@keyframes ctaSheen': {
                    '0%': { transform: 'translateX(-80%)' },
                    '45%': { transform: 'translateX(80%)' },
                    '100%': { transform: 'translateX(80%)' },
                  },
                }}
              >
                Create Account
              </Button>
              <Button variant="outlined" onClick={onSignIn} endIcon={<ArrowIcon />} sx={{ ...pillBtnSx, px: 2.6 }}>
                Sign in
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" label="Built on Ethereum" sx={{ backgroundColor: 'rgba(168,85,247,0.10)', border: `1px solid ${COLORS.border}` }} />
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component={motion.div}
              initial={reduceMotion ? false : { opacity: 0, y: 14, scale: 0.98 }}
              animate={reduceMotion ? false : { opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'visible',
              }}
            >
              {/* Orbiting nodes */}
              {!reduceMotion ? (
                <Box
                  component={motion.div}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: { xs: 360, md: 460 },
                    height: { xs: 360, md: 460 },
                    transform: 'translate(-50%, -50%)',
                    borderRadius: 999,
                    zIndex: 1,
                    pointerEvents: 'none',
                  }}
                >
                  {Array.from({ length: 8 }).map((_, i) => {
                    const angle = (i / 8) * Math.PI * 2;
                    const x = 50 + Math.cos(angle) * 47;
                    const y = 50 + Math.sin(angle) * 47;
                    return (
                      <Box
                        key={i}
                        component={motion.div}
                        animate={{ scale: [1, 1.12, 1] }}
                        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: i * 0.18 }}
                        sx={{
                          position: 'absolute',
                          left: `${x}%`,
                          top: `${y}%`,
                          transform: 'translate(-50%, -50%)',
                          width: 42,
                          height: 32,
                          borderRadius: 2.5,
                          border: '1px solid rgba(168,85,247,0.32)',
                          backgroundColor: 'rgba(13,15,24,0.70)',
                          boxShadow: '0 16px 60px rgba(0,0,0,0.60)',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: 11,
                            width: 7,
                            height: 7,
                            borderRadius: 999,
                            backgroundImage:
                              'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(34, 211, 238, 1) 100%)',
                            boxShadow: '0 0 18px rgba(34,211,238,0.35)',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              ) : null}

              <Box
                component={motion.div}
                animate={
                  reduceMotion
                    ? {}
                    : {
                        y: [0, -8, 0],
                      }
                }
                transition={reduceMotion ? {} : { duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
                sx={{ borderRadius: 4 }}
              >
                <EthereumGraphic />
              </Box>
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: -24,
                  zIndex: -1,
                  borderRadius: 6,
                  background:
                    'radial-gradient(380px 260px at 55% 40%, rgba(168,85,247,0.28) 0%, rgba(0,0,0,0) 60%), radial-gradient(360px 240px at 70% 60%, rgba(34,211,238,0.16) 0%, rgba(0,0,0,0) 60%)',
                  filter: 'blur(12px)',
                  opacity: 0.75,
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Feature cards row */}
        <Box id="features" />
        <Card
          variant="outlined"
          component={motion.div}
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          whileInView={reduceMotion ? {} : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          sx={{ ...surfaceCardSx, mt: { xs: 4, md: 5 } }}
        >
          <CardContent sx={{ p: { xs: 1.25, md: 1.5 } }}>
            <Grid container spacing={0} sx={{ alignItems: 'stretch' }}>
              {[
                { icon: <RocketIcon />, title: 'Launch in Minutes', desc: 'Launch your token quickly and easily.' },
                { icon: <GroupIcon />, title: 'Up to 50 Wallets', desc: 'Bundle and distribute supply to up to 50 wallets.' },
                { icon: <TuneIcon />, title: 'Full Control Panel', desc: 'Manage all wallets, allocations, and execution in one place.' },
                { icon: <PriceIcon />, title: 'Flat $600 Fee', desc: 'One simple price. No hidden costs.' },
              ].map((f, idx, arr) => (
                <Grid
                  key={f.title}
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{
                    px: { xs: 1.25, md: 1.75 },
                    py: { xs: 1.25, md: 1.5 },
                    borderRight: { md: idx < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' },
                    borderBottom: { xs: idx < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none', md: 'none' },
                  }}
                >
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 999,
                        display: 'grid',
                        placeItems: 'center',
                        background:
                          'radial-gradient(28px 28px at 50% 50%, rgba(168,85,247,0.24) 0%, rgba(0,0,0,0) 70%), rgba(168,85,247,0.10)',
                        border: '1px solid rgba(168,85,247,0.24)',
                        color: 'rgba(233,231,255,0.92)',
                        flexShrink: 0,
                      }}
                    >
                      {f.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                        {f.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, color: COLORS.textMuted }}>
                        {f.desc}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Container>

      {/* How it works */}
      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 8 } }}>
        <Box id="how" />
        <Typography variant="h5" sx={{ fontWeight: 900, textAlign: 'center', mb: 3 }}>
          How It Works
        </Typography>
        <Card variant="outlined" sx={{ ...surfaceCardSx, mt: 1.5 }}>
          <CardContent sx={{ p: { xs: 1.25, md: 1.5 } }}>
            <Grid container spacing={0} sx={{ alignItems: 'stretch' }}>
              {[
                { n: '1', icon: <SparkIcon />, title: 'Enter Token Info', desc: 'Add your token details and launch settings.' },
                { n: '2', icon: <RocketIcon />, title: 'Launch Instantly', desc: 'Deploy your token and bundle supply in seconds.' },
                { n: '3', icon: <InsightsIcon />, title: 'Manage Wallets', desc: 'Control wallets, track activity, and manage sells.' },
              ].map((s, idx, arr) => (
                <Grid
                  key={s.n}
                  item
                  xs={12}
                  md={4}
                  sx={{
                    px: { xs: 1.25, md: 1.75 },
                    py: { xs: 1.25, md: 1.5 },
                    borderRight: { md: idx < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' },
                    borderBottom: { xs: idx < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', md: 'none' },
                  }}
                >
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 999,
                        display: 'grid',
                        placeItems: 'center',
                        backgroundColor: 'rgba(168,85,247,0.14)',
                        border: '1px solid rgba(168,85,247,0.24)',
                        fontWeight: 900,
                        flexShrink: 0,
                      }}
                    >
                      {s.n}
                    </Box>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 3,
                        display: 'grid',
                        placeItems: 'center',
                        border: '1px solid rgba(168,85,247,0.20)',
                        background:
                          'linear-gradient(180deg, rgba(168,85,247,0.14) 0%, rgba(34,211,238,0.06) 100%)',
                        color: 'rgba(233,231,255,0.92)',
                        flexShrink: 0,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                        {s.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25, color: COLORS.textMuted }}>
                        {s.desc}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={featuredPanelSx}>
          <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: 1.4,
                    fontWeight: 800,
                    color: 'rgba(168,85,247,0.95)',
                    fontSize: { xs: 12.5, md: 14 },
                  }}
                >
                  SEE HOW EASY IT IS
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 950,
                    mt: 1,
                    lineHeight: 1.02,
                    letterSpacing: -0.3,
                    fontSize: { xs: 38, sm: 46, md: 54 },
                  }}
                >
                  Launch in Just
                  <br />
                  a Few Clicks
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.1,
                    color: COLORS.textMuted,
                    fontSize: { xs: 15.5, md: 17 },
                    lineHeight: 1.65,
                    maxWidth: 380,
                  }}
                >
                  A simple control panel gets your token live in minutes — not hours.
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 2.5 }}>
                  {[
                    'Enter token information',
                    'Set your supply and distribution',
                    'Choose up to 50 wallets',
                    'Review and launch',
                  ].map((t) => (
                    <Stack key={t} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: 999,
                          display: 'grid',
                          placeItems: 'center',
                          border: '1px solid rgba(168,85,247,0.55)',
                          backgroundColor: 'rgba(168,85,247,0.10)',
                          boxShadow: '0 0 0 3px rgba(168,85,247,0.04) inset',
                          flexShrink: 0,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 15, color: 'rgba(168,85,247,0.95)' }} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(233,231,255,0.78)',
                          fontSize: { xs: 15.5, md: 17 },
                          lineHeight: 1.35,
                        }}
                      >
                        {t}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={8}>
                <Box
                  sx={{
                    height: { xs: 250, md: 312 },
                    borderRadius: 4,
                    border: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(7, 8, 12, 0.65)',
                    boxShadow: '0 18px 70px rgba(0,0,0,0.55)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -18px 40px rgba(0,0,0,0.55)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/launch-preview.png"
                    alt=""
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: { xs: 'scale(1.02)', md: 'scale(1.01)' },
                      filter: 'saturate(1.05) contrast(1.05)',
                      display: 'block',
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box id="pricing" />
        <Card
          variant="outlined"
          sx={{
            ...surfaceCardSx,
            mt: 3,
            backgroundImage:
              'linear-gradient(180deg, rgba(13, 15, 24, 0.70) 0%, rgba(8, 9, 14, 0.70) 100%)',
            boxShadow: '0 16px 70px rgba(0,0,0,0.50)',
          }}
        >
          <CardContent sx={{ p: { xs: 2.25, md: 2.75 } }}>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'flex-start', md: 'center' } }}>
              <Grid
                container
                alignItems="center"
                justifyContent={{ xs: 'flex-start', md: 'center' }}
                columnSpacing={2.25}
                rowSpacing={1.75}
                sx={{ minHeight: { xs: 90, md: 96 }, maxWidth: 980 }}
              >
              <Grid item>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 999,
                    display: 'grid',
                    placeItems: 'center',
                    border: '1px solid rgba(168,85,247,0.28)',
                    background:
                      'radial-gradient(36px 36px at 50% 50%, rgba(168,85,247,0.20) 0%, rgba(0,0,0,0) 72%), rgba(168,85,247,0.06)',
                    color: 'rgba(233,231,255,0.92)',
                    flexShrink: 0,
                  }}
                >
                  <PriceIcon />
                </Box>
              </Grid>

              <Grid item sx={{ minWidth: 220 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 950,
                    lineHeight: 1,
                    color: '#a855f7',
                    textShadow: '0 10px 40px rgba(168,85,247,0.25)',
                  }}
                >
                  $600
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(168,85,247,0.85)', fontWeight: 650 }}>
                  Per Launch
                </Typography>
              </Grid>

              <Grid item sx={{ display: { xs: 'none', md: 'block' } }}>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: 52,
                    opacity: 0.16,
                    borderColor: 'rgba(255,255,255,0.14)',
                  }}
                />
              </Grid>

              <Grid item xs={12} md sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minHeight: { xs: 0, md: 52 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                    No hidden fees. No surprises.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, color: COLORS.textMuted }}>
                    Just a simple, flat rate to launch your token.
                  </Typography>
                </Box>
              </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          sx={{
            ...surfaceCardSx,
            mt: 3,
            backgroundImage:
              'linear-gradient(180deg, rgba(13, 15, 24, 0.70) 0%, rgba(8, 9, 14, 0.70) 100%)',
            boxShadow: '0 16px 70px rgba(0,0,0,0.50)',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              p: { xs: 2.25, md: 2.75 },
            }}
          >
            <Stack direction="row" spacing={1.75} sx={{ alignItems: 'center' }}>
              <Box
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  background:
                    'radial-gradient(40px 40px at 50% 45%, rgba(168,85,247,0.22) 0%, rgba(0,0,0,0) 70%), rgba(168,85,247,0.06)',
                  border: '1px solid rgba(168,85,247,0.30)',
                  boxShadow: '0 0 0 6px rgba(168,85,247,0.03) inset',
                  flexShrink: 0,
                }}
              >
                <RocketIcon sx={{ color: 'rgba(233,231,255,0.92)' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  Ready to launch your token?
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25, color: COLORS.textMuted }}>
                  Create an account and start launching in minutes.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              onClick={onSignUp}
              endIcon={<ArrowIcon />}
              sx={{
                borderRadius: 999,
                px: 3.8,
                py: 1.15,
                minWidth: { xs: '100%', sm: 260 },
                justifyContent: 'center',
                textTransform: 'none',
                fontWeight: 800,
                backgroundImage: 'linear-gradient(90deg, rgba(168,85,247,1) 0%, rgba(59,130,246,1) 100%)',
                boxShadow: '0 18px 55px rgba(59,130,246,0.18), 0 18px 55px rgba(168,85,247,0.14)',
                '&:hover': {
                  backgroundImage: 'linear-gradient(90deg, rgba(192,132,252,1) 0%, rgba(96,165,250,1) 100%)',
                },
              }}
            >
              Create Account
            </Button>
          </CardContent>
        </Card>
      </Container>

      {/* Footer */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <BrandLogo size={22} showText={false} />
            <Typography variant="body2" color="text.secondary">
              Bundle supply. Dominate the launch.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;

